import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import axiosClient from '../../api/axiosClient';
import { getProfileImageUrl } from '../../utils/imageUrl';
import MobileBottomNav from '../MobileBottomNav';
import Breadcrumb from './Breadcrumb';
import WhatsAppWidget from './WhatsAppWidget';
import './Header.css';

const Header = () => {
  const { user, profile, logout } = useAuth();
  const { showSuccess } = useToast();
  const [scrolled, setScrolled] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    setMobileMenuOpen(false);
    setShowUserDropdown(false);
    await logout();
    showSuccess('Logged out successfully!');
    navigate('/matrimony');
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setShowNotifications(false);
    setShowUserDropdown(false);
  }, [location.pathname]);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axiosClient.get('/matrimony/notifications');
      if (res.data && res.data.data) {
        setNotifications(res.data.data.notifications || []);
        setUnreadCount(res.data.data.unreadCount || 0);
      }
    } catch (err) {
      // Fallback silently
    }
  }, [user]);

  useEffect(() => {
    if (user) fetchNotifications();
  }, [user, fetchNotifications]);

  const handleMarkAllRead = async () => {
    try {
      await axiosClient.patch('/matrimony/notifications/mark-read');
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch (err) {
      setUnreadCount(0);
    }
  };

  const dummyNotifications = [
    { id: 1, text: 'Karthik sent you an Interest request', time: '10m ago' },
    { id: 2, text: 'Your profile photo was verified successfully', time: '1h ago' },
    { id: 3, text: '5 new matches found in Coimbatore', time: '2h ago' },
  ];

  const getHomeLink = () => {
    if (!user) {
      if (location.pathname === '/' || location.pathname === '/community') return '/';
      return '/matrimony';
    }
    if (!profile || profile.profileStatus === 'Draft') return '/matrimony/complete-profile';
    if (profile.verificationStatus === 'Pending') return '/matrimony/verification-pending';
    return '/matrimony/dashboard';
  };

  return (
    <>
      {/* ── STICKY HEADER ── */}
      <header
        className="navbar sticky-top w-100"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1050,
          padding: scrolled ? '8px 0' : '12px 0',
          background: '#ffffff',
          borderBottom: '1px solid #edf2f7',
          boxShadow: scrolled
            ? '0 4px 20px rgba(0, 0, 0, 0.08)'
            : '0 2px 10px rgba(0, 0, 0, 0.04)',
          transition: 'all 0.3s ease-in-out',
        }}
      >
        <div className="container d-flex align-items-center justify-content-between flex-nowrap px-2 px-sm-3">

          {/* ── BRAND LOGO ── */}
          <Link
            to={getHomeLink()}
            className="navbar-brand d-flex align-items-center text-decoration-none me-1 me-sm-3 flex-shrink-0"
          >
            <div className="position-relative me-2">
              <img
                src="/assets/images/logo.jpg"
                alt="Logo"
                className="rounded-circle shadow-sm header-logo-img border"
                style={{
                  width: scrolled ? '34px' : '38px',
                  height: scrolled ? '34px' : '38px',
                  border: '1.5px solid rgba(219, 33, 76, 0.35)',
                  objectFit: 'cover'
                }}
              />
            </div>
            <div>
              <span className="fw-bold d-block text-dark header-brand-title">
                ANUPPAGOUNDAR
              </span>
              <span className="d-block header-brand-sub">
                Exclusive Matrimony
              </span>
            </div>
          </Link>

          {/* ── RIGHT SECTION: AUTH-BASED NAV ── */}
          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            {user ? (
              <>
                {profile?.verificationStatus === 'Pending' ? (
                  /* ── PENDING STATE ── */
                  <>
                    <span
                      className="d-none d-sm-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill small fw-bold"
                      style={{
                        background: 'rgba(219, 33, 76, 0.08)',
                        border: '1.5px solid rgb(219, 33, 76)',
                        color: 'rgb(219, 33, 76)',
                        fontSize: '0.8rem',
                      }}
                    >
                      <i className="fa fa-shield me-1"></i> Under Review
                    </span>
                    <button onClick={handleLogout} className="btn btn-logout px-3 py-1 small">
                      <i className="fa fa-sign-out me-1"></i> Logout
                    </button>
                  </>
                ) : (
                  /* ── VERIFIED / ACTIVE STATE ── */
                  <>
                    {/* Desktop Nav Links */}
                    <nav className="d-none d-md-flex align-items-center gap-3 me-2">
                      <Link
                        to="/matrimony/search"
                        className={`nav-link ${location.pathname.startsWith('/matrimony/search') ? 'text-danger fw-bold' : 'text-dark'}`}
                        style={{ fontSize: '0.92rem' }}
                      >
                        Search Profiles
                      </Link>
                      <Link
                        to="/matrimony/dashboard"
                        className={`nav-link ${location.pathname.startsWith('/matrimony/dashboard') ? 'text-danger fw-bold' : 'text-dark'}`}
                        style={{ fontSize: '0.92rem' }}
                      >
                        Dashboard
                      </Link>
                      <Link
                        to="/matrimony/plans"
                        className={`nav-link ${location.pathname.startsWith('/matrimony/plans') ? 'text-danger fw-bold' : 'text-dark'}`}
                        style={{ fontSize: '0.92rem' }}
                      >
                        Upgrade Plan
                      </Link>
                    </nav>

                    {/* ── NOTIFICATION BELL ── */}
                    <div className="position-relative">
                      <button
                        type="button"
                        className="btn p-1 text-dark position-relative border-0"
                        onClick={() => setShowNotifications(!showNotifications)}
                        aria-label="View Notifications"
                      >
                        <i className="fa fa-bell-o fs-5" style={{ color: '#0f172a' }}></i>
                        {unreadCount > 0 && (
                          <span
                            className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                            style={{ fontSize: '10px' }}
                          >
                            {unreadCount}
                          </span>
                        )}
                      </button>

                      {showNotifications && (
                        <>
                          <div
                            className="position-fixed top-0 start-0 w-100 h-100"
                            style={{ zIndex: 1080 }}
                            onClick={() => setShowNotifications(false)}
                          />
                          <div
                            className="position-absolute end-0 mt-2 bg-white rounded-3 shadow-lg border overflow-hidden"
                            style={{ width: '300px', zIndex: 1100 }}
                          >
                            <div className="p-2 border-bottom bg-light d-flex justify-content-between align-items-center">
                              <span className="fw-bold small text-dark">Notifications</span>
                              {unreadCount > 0 && (
                                <span className="badge bg-danger rounded-pill">{unreadCount} new</span>
                              )}
                            </div>
                            <div className="list-group list-group-flush" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                              {notifications.length > 0 ? (
                                notifications.map((n) => (
                                  <div key={n._id || n.id} className="list-group-item p-2">
                                    <p className="mb-1 small text-dark" style={{ fontSize: '0.8rem' }}>
                                      {n.message || n.text}
                                    </p>
                                    <small className="text-muted" style={{ fontSize: '10px' }}>
                                      {n.createdAt ? new Date(n.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : n.time}
                                    </small>
                                  </div>
                                ))
                              ) : (
                                dummyNotifications.map((notif) => (
                                  <div key={notif.id} className="list-group-item list-group-item-action p-2">
                                    <p className="mb-1 small fw-semibold text-dark" style={{ fontSize: '0.8rem' }}>
                                      {notif.text}
                                    </p>
                                    <small className="text-muted" style={{ fontSize: '10px' }}>{notif.time}</small>
                                  </div>
                                ))
                              )}
                            </div>
                            <div className="p-2 text-center border-top bg-light">
                              <small
                                className="fw-bold text-danger"
                                style={{ cursor: 'pointer' }}
                                onClick={handleMarkAllRead}
                              >
                                Mark all as read
                              </small>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* ── USER AVATAR DROPDOWN (Desktop) ── */}
                    <div className="d-none d-md-block position-relative ms-2">
                      <button
                        type="button"
                        className="btn p-0 border-0 bg-transparent d-flex align-items-center"
                        onClick={() => setShowUserDropdown(!showUserDropdown)}
                        title="User Account Menu"
                      >
                        <img
                          src={getProfileImageUrl(
                            profile?.profilePhoto,
                            user?.avatar || user?.picture,
                            profile?.gender
                          )}
                          className="rounded-circle border border-2 border-danger"
                          alt="Profile"
                          style={{ width: '36px', height: '36px', objectFit: 'cover', cursor: 'pointer' }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/assets/images/avatar_person.png';
                          }}
                        />
                      </button>

                      {showUserDropdown && (
                        <>
                          <div
                            className="position-fixed top-0 start-0 w-100 h-100"
                            style={{ zIndex: 1080 }}
                            onClick={() => setShowUserDropdown(false)}
                          />
                          <div
                            className="position-absolute end-0 mt-2 bg-white rounded-3 shadow-lg border p-2 overflow-hidden"
                            style={{ width: '210px', zIndex: 1100 }}
                          >
                            <button
                              type="button"
                              className="dropdown-item py-1 px-2 rounded-2 small fw-bold text-danger d-flex align-items-center gap-2 w-100 text-start"
                              onClick={() => {
                                setShowUserDropdown(false);
                                handleLogout();
                              }}
                            >
                              <i className="fa fa-sign-out"></i> Logout
                            </button>
                          </div>
                        </>
                      )}
                    </div>

                    {/* ── MOBILE HAMBURGER ── */}
                    <button
                      type="button"
                      className="btn d-md-none border-0 p-1 text-dark"
                      onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                      aria-label="Toggle Navigation"
                    >
                      <i className={`fa ${mobileMenuOpen ? 'fa-times' : 'fa-bars'} fs-4`}></i>
                    </button>
                  </>
                )}
              </>
            ) : (
              /* ── GUEST (NOT LOGGED IN) ── */
              <div className="d-flex align-items-center gap-1 gap-sm-2 flex-shrink-0">
                {location.pathname === '/matrimony/login' || location.pathname === '/login' ? (
                  <>
                    <span className="text-secondary small fw-semibold d-none d-sm-inline me-1">
                      New User?
                    </span>
                    <Link
                      to="/matrimony/signup"
                      className="btn btn-brand px-2.5 px-sm-3 py-1 rounded-pill shadow-sm small text-nowrap d-inline-flex align-items-center"
                      style={{ fontSize: '0.84rem' }}
                    >
                      <i className="fa fa-user-plus me-1"></i> Register Free
                    </Link>
                  </>
                ) : location.pathname === '/matrimony/signup' || location.pathname === '/signup' ? (
                  <>
                    <span className="text-secondary small fw-semibold d-none d-sm-inline me-1">
                      Already a member?
                    </span>
                    <Link
                      to="/matrimony/login"
                      className="btn btn-brand px-2.5 px-sm-3 py-1 rounded-pill shadow-sm small text-nowrap d-inline-flex align-items-center"
                      style={{ fontSize: '0.84rem' }}
                    >
                      <i className="fa fa-sign-in me-1"></i> Sign In
                    </Link>
                  </>
                ) : (
                  <>
                    <Link
                      to="/matrimony/login"
                      className="btn btn-brand-outline px-2.5 px-sm-3 py-1 rounded-pill small text-decoration-none text-nowrap d-inline-flex align-items-center"
                      style={{ fontSize: '0.84rem' }}
                    >
                      <span className="header-signin-gradient-text">
                        <i className="fa fa-sign-in me-1"></i> Sign In
                      </span>
                    </Link>
                    <Link
                      to="/matrimony/signup"
                      className="btn btn-brand px-2.5 px-sm-3 py-1 rounded-pill shadow-sm small text-nowrap d-inline-flex align-items-center"
                      style={{ fontSize: '0.84rem' }}
                    >
                      <i className="fa fa-user-plus me-1"></i> Register Free
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── MOBILE SLIDE-OUT DRAWER ── */}
        {mobileMenuOpen && user && (
          <div
            className="d-md-none position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
            style={{ zIndex: 1060 }}
            onClick={() => setMobileMenuOpen(false)}
          >
            <div
              className="position-absolute top-0 end-0 h-100 bg-white shadow-lg p-4"
              style={{ width: '280px', maxWidth: '85vw', animation: 'slideInRight 0.25s ease-out' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drawer Header */}
              <div className="d-flex align-items-center justify-content-between pb-3 border-bottom mb-3">
                <button
                  type="button"
                  className="btn-close ms-auto"
                  onClick={() => setMobileMenuOpen(false)}
                ></button>
              </div>

              <div className="d-flex flex-column gap-2">
                <div className="d-flex align-items-center gap-3 p-2 bg-light rounded-3 mb-2">
                  <img
                    src={getProfileImageUrl(
                      profile?.profilePhoto,
                      user?.avatar || user?.picture,
                      profile?.gender
                    )}
                    className="rounded-circle border"
                    alt="Avatar"
                    style={{ width: '42px', height: '42px', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/assets/images/avatar_person.png';
                    }}
                  />
                  <div>
                    <h6 className="fw-semibold mb-0 text-dark small">
                      {profile?.firstName
                        ? `${profile.firstName} ${profile.lastName || ''}`.trim()
                        : 'User Profile'}
                    </h6>
                    <small className="text-muted" style={{ fontSize: '11px' }}>
                      {user.email}
                    </small>
                  </div>
                </div>

                <Link
                  to={getHomeLink()}
                  className="btn btn-light text-start py-2 px-3 rounded-3 text-dark fw-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <i className="fa fa-home me-2 text-danger"></i> Dashboard
                </Link>

                <Link
                  to="/search"
                  className="btn btn-light text-start py-2 px-3 rounded-3 text-dark fw-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <i className="fa fa-search me-2 text-danger"></i> Search Profiles
                </Link>

                <Link
                  to="/plans"
                  className="btn btn-light text-start py-2 px-3 rounded-3 text-dark fw-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <i className="fa fa-diamond me-2 text-warning"></i> Upgrade Membership
                </Link>

                <Link
                  to="/settings"
                  className="btn btn-light text-start py-2 px-3 rounded-3 text-dark fw-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <i className="fa fa-sliders me-2 text-secondary"></i> Settings
                </Link>

                <button
                  onClick={handleLogout}
                  className="btn btn-outline-danger w-100 rounded-pill py-2 mt-3 fw-semibold"
                >
                  <i className="fa fa-sign-out me-2"></i> Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Global Matrimony Responsive Breadcrumb Navigation */}
      <Breadcrumb />

      {/* Global Mobile Bottom Navigation */}
      <MobileBottomNav />

      {/* Global Floating WhatsApp Contact Action */}
      <WhatsAppWidget />
    </>
  );
};

export default Header;
